import { useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ProBadge } from "@/components/ui/ProBadge";
import { Select } from "@/components/ui/select";
import { AvatarUploader } from "@/components/ui/AvatarUploader";
import { Button } from "@/components/ui/button";
import {
  Layout,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image as ImageIcon,
  UploadCloud,
  Check,
  Type,
  Sliders,
} from "lucide-react";
import type { HeroOverrides } from "@profolio/templates/types";
import { getStyleRecipe } from "@profolio/templates";
import type { ThemeKey } from "@profolio/validators";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";

interface HeroCustomizerProps {
  hero: HeroOverrides;
  userTier?: string;
  currentThemeKey?: ThemeKey | string | null;
  styleRecipe?: string | null;
  currentPhotoUrl?: string | null;
  fullName?: string;
  onPhotoUpdate?: (newPhotoUrl: string) => void;
  onFullNameUpdate?: (newFullName: string) => void;
  onUpdate: <K extends keyof HeroOverrides>(key: K, value: HeroOverrides[K]) => void;
  onUpgradeClick: (featureKey?: string) => void;
}

interface HeroLayoutDefinition {
  id: NonNullable<HeroOverrides["heroLayout"]>;
  label: string;
  desc: string;
  nativeThemes: ThemeKey[];
}

const HERO_LAYOUTS: readonly HeroLayoutDefinition[] = [
  {
    id: "standard",
    label: "Centered Stack",
    desc: "Traditional centered column (Avatar -> Name -> Bio)",
    nativeThemes: ["classic-academic", "minimalist-scholar", "grid-showcase"],
  },
  {
    id: "split-left",
    label: "Split Left",
    desc: "Two-column editorial (Photo left, Text & actions right)",
    nativeThemes: ["classic-academic", "modern-bento", "minimalist-scholar"],
  },
  {
    id: "split-right",
    label: "Split Right",
    desc: "Two-column editorial (Text left, Photo right)",
    nativeThemes: ["modern-executive"],
  },
  {
    id: "banner",
    label: "Full-Bleed Banner",
    desc: "Hero image banner with dark tint overlay & typography",
    nativeThemes: ["classic-academic", "modern-executive"],
  },
  {
    id: "card",
    label: "Elevated Card",
    desc: "Self-contained framed glass card with subtle elevation",
    nativeThemes: ["modern-bento", "modern-executive"],
  },
  {
    id: "minimal",
    label: "Minimalist Typographic",
    desc: "High-impact name & links without portrait photo",
    nativeThemes: ["minimalist-scholar"],
  },
] as const;

function HeroLayoutThumbnail({ layoutId }: { layoutId: string }) {
  return (
    <div className="w-full h-12 rounded-md bg-muted/40 border border-border/50 p-1.5 flex items-center justify-center mb-1.5 select-none pointer-events-none">
      {layoutId === "standard" && (
        <div className="flex flex-col items-center gap-1 w-full">
          <div className="h-3.5 w-3.5 rounded-full bg-primary/40 border border-primary/60" />
          <div className="h-1 w-12 rounded bg-foreground/40" />
          <div className="h-0.5 w-8 rounded bg-muted-foreground/30" />
        </div>
      )}
      {layoutId === "split-left" && (
        <div className="flex items-center gap-2 w-full px-2">
          <div className="h-5 w-5 rounded-full bg-primary/40 border border-primary/60 shrink-0" />
          <div className="flex-1 space-y-1">
            <div className="h-1 w-full rounded bg-foreground/40" />
            <div className="h-0.5 w-3/4 rounded bg-muted-foreground/30" />
          </div>
        </div>
      )}
      {layoutId === "split-right" && (
        <div className="flex items-center gap-2 w-full px-2">
          <div className="flex-1 space-y-1">
            <div className="h-1 w-full rounded bg-foreground/40" />
            <div className="h-0.5 w-3/4 rounded bg-muted-foreground/30" />
          </div>
          <div className="h-5 w-5 rounded-full bg-primary/40 border border-primary/60 shrink-0" />
        </div>
      )}
      {layoutId === "banner" && (
        <div className="flex flex-col w-full h-full rounded overflow-hidden border border-border/40">
          <div className="h-1/2 bg-primary/25 flex items-center justify-center" />
          <div className="h-1/2 bg-card p-1 flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-primary/60 shrink-0" />
            <div className="h-1 w-10 rounded bg-foreground/40" />
          </div>
        </div>
      )}
      {layoutId === "card" && (
        <div className="w-5/6 h-full rounded border border-primary/40 bg-card p-1 flex items-center gap-1.5 shadow-2xs">
          <div className="h-4 w-4 rounded-full bg-primary/30 shrink-0" />
          <div className="flex-1 space-y-0.5">
            <div className="h-1 w-full rounded bg-foreground/40" />
            <div className="h-0.5 w-2/3 rounded bg-muted-foreground/30" />
          </div>
        </div>
      )}
      {layoutId === "minimal" && (
        <div className="flex flex-col items-center gap-1 w-full">
          <div className="h-1.5 w-16 rounded bg-foreground/60" />
          <div className="h-1 w-10 rounded bg-muted-foreground/40" />
          <div className="flex gap-1 mt-0.5">
            <div className="h-1 w-3 rounded-full bg-primary/40" />
            <div className="h-1 w-3 rounded-full bg-primary/40" />
          </div>
        </div>
      )}
    </div>
  );
}

export function HeroCustomizer({
  hero = {},
  userTier = "starter",
  currentThemeKey = "classic-academic",
  styleRecipe,
  currentPhotoUrl,
  fullName = "Scholar",
  onPhotoUpdate,
  onFullNameUpdate,
  onUpdate,
  onUpgradeClick,
}: HeroCustomizerProps) {
  const productTier = mapSubscriptionToProductTier(userTier);
  const isPro = productTier === "pro" || productTier === "premium";
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const bannerFileInputRef = useRef<HTMLInputElement | null>(null);

  const recipe = styleRecipe ? getStyleRecipe(styleRecipe) : null;
  const defaultAvatarShape = recipe?.heroDefaults?.avatarShape || "circle";
  const effectiveAvatarShape = hero.avatarShape || defaultAvatarShape;

  const defaultAvatarBorder = recipe?.heroDefaults?.avatarBorder || "none";
  const effectiveAvatarBorder = hero.avatarBorder !== undefined ? (hero.avatarBorder || "none") : defaultAvatarBorder;

  const defaultAvatarShadow = recipe?.heroDefaults?.avatarShadow || "none";
  const effectiveAvatarShadow = hero.avatarShadow !== undefined ? (hero.avatarShadow || "none") : defaultAvatarShadow;

  const defaultAvatarBorderGradient = recipe?.heroDefaults?.avatarBorderGradient || "none";
  const effectiveAvatarBorderGradient = hero.avatarBorderGradient !== undefined ? (hero.avatarBorderGradient || "none") : defaultAvatarBorderGradient;

  const getAvatarNumericSize = (sizeKey?: string): number => {
    if (!sizeKey) return 96;
    const map: Record<string, number> = {
      xs: 48,
      sm: 64,
      md: 96,
      lg: 128,
      xl: 160,
      "2xl": 200,
      "3xl": 240,
      "4xl": 280,
      "5xl": 320,
      "6xl": 384,
      max: 450,
    };
    if (map[sizeKey]) return map[sizeKey];
    const parsed = parseInt(sizeKey, 10);
    return isNaN(parsed) ? 96 : parsed;
  };
  const currentNumericSize = getAvatarNumericSize(hero.avatarSize);

  const getUploadUrlMutation = trpc.media.getUploadUrl.useMutation();
  const confirmUploadMutation = trpc.media.confirmUpload.useMutation();

  const handleFieldChange = <K extends keyof HeroOverrides>(key: K, value: HeroOverrides[K]) => {
    if (!isPro) {
      onUpgradeClick("layout_overrides");
      return;
    }
    onUpdate(key, value);
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isPro) {
      onUpgradeClick("layout_overrides");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Banner image must be under 10MB");
      return;
    }

    try {
      setIsUploadingBanner(true);
      toast.info("Uploading banner image to Cloudflare R2...");

      const { url, objectKey } = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type || "image/jpeg",
        fileSize: file.size,
        category: "figure",
      });

      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "image/jpeg" },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload image to storage");
      }

      const confirmRes = await confirmUploadMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type || "image/jpeg",
        fileSize: file.size,
        category: "figure",
        objectKey,
      });

      handleFieldChange("heroBackgroundImageUrl", confirmRes.media.r2Url);
      toast.success("Banner image uploaded successfully");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to upload banner image";
      toast.error(msg);
    } finally {
      setIsUploadingBanner(false);
      if (bannerFileInputRef.current) {
        bannerFileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Hero Layout Archetype ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Layout className="h-3.5 w-3.5 text-primary" />
              Hero Layout Structure
            </h4>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Choose how your identity, portrait, and bio are composed.
            </p>
          </div>
          {!isPro && <ProBadge locked />}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {HERO_LAYOUTS.map((item) => {
            const isSelected = (hero.heroLayout || "standard") === item.id;
            const isNative = item.nativeThemes.includes(currentThemeKey as ThemeKey);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleFieldChange("heroLayout", item.id)}
                className={`p-2.5 rounded-lg border text-left transition-all relative cursor-pointer ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary shadow-xs"
                    : !isPro
                    ? "border-border/60 bg-muted/20 opacity-75 hover:opacity-100"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <HeroLayoutThumbnail layoutId={item.id} />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground flex items-center gap-1.5 truncate">
                    {item.label}
                    {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                  </span>
                  {isNative && (
                    <span className="text-[9px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800 shrink-0">
                      Native
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Banner Image Media (When banner layout selected) ── */}
      {hero.heroLayout === "banner" && (
        <div className="space-y-3 p-3.5 bg-muted/30 border rounded-xl animate-in fade-in duration-200">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold flex items-center gap-1.5">
              <ImageIcon className="h-3.5 w-3.5 text-primary" />
              Banner Background Image
            </Label>
            {!isPro && <ProBadge locked />}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={bannerFileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleBannerUpload}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!isPro || isUploadingBanner}
              onClick={() => bannerFileInputRef.current?.click()}
              className="gap-1.5 text-xs h-8 shrink-0"
            >
              <UploadCloud className="h-3.5 w-3.5" />
              {isUploadingBanner ? "Uploading..." : "Upload Banner to R2"}
            </Button>
            <input
              type="text"
              className="flex-1 text-xs p-2 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono"
              placeholder="https://... (or direct image URL)"
              value={hero.heroBackgroundImageUrl || ""}
              disabled={!isPro}
              onChange={(e) => handleFieldChange("heroBackgroundImageUrl", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Dark Tint Overlay</Label>
              <Select
                value={hero.heroBackgroundOverlay || "rgba(0,0,0,0.6)"}
                disabled={!isPro}
                onChange={(e) => handleFieldChange("heroBackgroundOverlay", e.target.value)}
                className="h-8 text-xs bg-card"
              >
                <option value="rgba(0,0,0,0.3)">Light Tint (30%)</option>
                <option value="rgba(0,0,0,0.6)">Standard Tint (60%)</option>
                <option value="rgba(0,0,0,0.85)">Heavy Tint (85%)</option>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Focal Position</Label>
              <Select
                value={hero.heroBackgroundPosition || "center"}
                disabled={!isPro}
                onChange={(e) =>
                  handleFieldChange(
                    "heroBackgroundPosition",
                    e.target.value as NonNullable<HeroOverrides["heroBackgroundPosition"]>
                  )
                }
                className="h-8 text-xs bg-card"
              >
                <option value="center">Center</option>
                <option value="top">Top Focal Point</option>
                <option value="bottom">Bottom Focal Point</option>
                <option value="cover">Cover Fill</option>
                <option value="contain">Contain Fit</option>
                <option value="fixed">Fixed (Stay Fixed / Parallax on Scroll)</option>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* ── 2. Avatar & Portrait Controls ── */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Portrait & Avatar Geometry
          </h4>
          {!isPro && <ProBadge locked />}
        </div>

        <div className="flex items-center justify-between">
          <Label className="text-xs">Display Profile Photo</Label>
          <Switch
            checked={hero.avatarVisible !== false}
            disabled={!isPro}
            onCheckedChange={(v) => handleFieldChange("avatarVisible", v)}
          />
        </div>

        {hero.avatarVisible !== false && (
          <div className="space-y-3.5">
            {onPhotoUpdate && (
              <div className="p-3 bg-muted/40 rounded-xl border border-border/60">
                <AvatarUploader
                  currentPhotoUrl={currentPhotoUrl}
                  fullName={fullName}
                  onSuccess={onPhotoUpdate}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Avatar Shape</Label>
                <Select
                  value={effectiveAvatarShape}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarShape",
                      e.target.value as NonNullable<HeroOverrides["avatarShape"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="circle">Circle (Round)</option>
                  <option value="squircle">Squircle (Smooth)</option>
                  <option value="rounded">Rounded Square</option>
                  <option value="square">Sharp Square</option>
                  <option value="hexagon">Hexagon</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Avatar Scale</Label>
                <Select
                  value={
                    ["xs", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "max"].includes(
                      hero.avatarSize || ""
                    )
                      ? hero.avatarSize
                      : "custom"
                  }
                  disabled={!isPro}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "custom") {
                      handleFieldChange("avatarSize", `${currentNumericSize || 200}px`);
                    } else {
                      handleFieldChange(
                        "avatarSize",
                        val as NonNullable<HeroOverrides["avatarSize"]>
                      );
                    }
                  }}
                  className="h-8 text-xs bg-card"
                >
                  <option value="xs">Extra Small (48px)</option>
                  <option value="sm">Small (64px)</option>
                  <option value="md">Medium (96px)</option>
                  <option value="lg">Large (128px)</option>
                  <option value="xl">Extra Large (160px)</option>
                  <option value="2xl">Jumbo (200px)</option>
                  <option value="3xl">2X Jumbo (240px)</option>
                  <option value="4xl">3X Jumbo (280px)</option>
                  <option value="5xl">Portrait (320px)</option>
                  <option value="6xl">Spotlight (384px)</option>
                  <option value="max">Maximum Studio (450px)</option>
                  <option value="custom">Custom Size ({currentNumericSize}px)</option>
                </Select>
              </div>
            </div>

            {/* Fine Granular Scale Slider & Stepper (Freedom to size beyond 200px up to 500px) */}
            <div className="space-y-1.5 p-2.5 rounded-lg bg-muted/40 border border-border/40">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[11px] font-medium text-foreground flex items-center gap-1.5">
                  <Sliders className="h-3.5 w-3.5 text-primary" />
                  <span>Avatar Dimensions</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={48}
                    max={500}
                    step={4}
                    disabled={!isPro}
                    value={currentNumericSize}
                    onChange={(e) => {
                      const val = Math.min(500, Math.max(48, Number(e.target.value) || 48));
                      handleFieldChange("avatarSize", `${val}px`);
                    }}
                    className="w-16 h-6 px-1.5 text-right text-xs bg-background rounded border border-border focus:border-primary outline-none font-mono"
                  />
                  <span className="text-[10px] text-muted-foreground font-mono">px</span>
                </div>
              </div>
              <input
                type="range"
                min={48}
                max={500}
                step={4}
                disabled={!isPro}
                value={currentNumericSize}
                onChange={(e) => handleFieldChange("avatarSize", `${e.target.value}px`)}
                className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-[9px] text-muted-foreground font-mono">
                <span>48px</span>
                <span>200px</span>
                <span>320px</span>
                <span>500px</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Border Treatment</Label>
                <Select
                  value={effectiveAvatarBorder}
                  onChange={(e) => {
                    const val = e.target.value as NonNullable<HeroOverrides["avatarBorder"]>;
                    if (val !== "none" && !isPro) {
                      onUpgradeClick("hero_avatar_styling");
                      return;
                    }
                    handleFieldChange("avatarBorder", val);
                  }}
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">None (Clean Edge / Borderless)</option>
                  <option value="thin">Thin Neutral</option>
                  <option value="medium">Medium Outline</option>
                  <option value="thick">Thick Solid</option>
                  <option value="accent">Theme Accent</option>
                  <option value="gradient">Gradient Glow Ring</option>
                  <option value="dashed">Dashed Academic</option>
                  <option value="double">Double Luxury Ring</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Alignment</Label>
                <Select
                  value={hero.avatarPosition || "center"}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarPosition",
                      e.target.value as NonNullable<HeroOverrides["avatarPosition"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="center">Center Aligned</option>
                  <option value="left">Left Aligned</option>
                  <option value="right">Right Aligned</option>
                </Select>
              </div>
            </div>

            {/* ── 3D Depth, Shadows & Animations ── */}
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border/50">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Shadow Elevation</Label>
                <Select
                  value={effectiveAvatarShadow}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarShadow",
                      e.target.value as NonNullable<HeroOverrides["avatarShadow"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">None</option>
                  <option value="subtle">Subtle Depth</option>
                  <option value="elevated">Elevated Shadow</option>
                  <option value="3d">3D Multi-Layer Depth</option>
                  <option value="glow">Atmospheric Glow</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Idle Animation</Label>
                <Select
                  value={hero.avatarAnimation || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarAnimation",
                      e.target.value as NonNullable<HeroOverrides["avatarAnimation"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">None (Static)</option>
                  <option value="float">Gentle Floating</option>
                  <option value="pulse-glow">Breathing Pulse Glow</option>
                  <option value="spin-ring">Conic Rotating Ring</option>
                </Select>
              </div>
            </div>

            {/* ── Border Gradient & 3D Tactile Bevel ── */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Border Gradient Preset</Label>
                <Select
                  value={effectiveAvatarBorderGradient}
                  disabled={!isPro || effectiveAvatarBorder === "none"}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarBorderGradient",
                      e.target.value as NonNullable<HeroOverrides["avatarBorderGradient"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">Default / Solid</option>
                  <option value="sunset">Sunset Gold</option>
                  <option value="aurora">Aurora Borealis</option>
                  <option value="cyber">Cyber Neon</option>
                  <option value="gold">Champagne Gold</option>
                  <option value="primary-accent">Theme Primary &bull; Accent</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">3D Convex Bevel (Tactile)</Label>
                <Select
                  value={hero.avatarBevel || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarBevel",
                      e.target.value as NonNullable<HeroOverrides["avatarBevel"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">None (Flat)</option>
                  <option value="light">Light (Subtle Crystal Rim)</option>
                  <option value="medium">Pinback Button Badge (Celluloid Dome &bull; Recommended)</option>
                  <option value="high">High (Deep Convex Dome)</option>
                </Select>
              </div>
            </div>

            <div className="pt-1">
              <label className="flex items-center gap-2 cursor-pointer h-7 text-xs font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={hero.avatar3dEffect === true}
                  disabled={!isPro}
                  onChange={(e) => handleFieldChange("avatar3dEffect", e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                />
                <span>Enable Interactive 3D Perspective Tilt on Hover</span>
              </label>
            </div>

            {/* ── Logo Background & Padding ── */}
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border/50">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Logo Canvas Background</Label>
                <Select
                  value={hero.avatarBgGradient || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarBgGradient",
                      e.target.value as NonNullable<HeroOverrides["avatarBgGradient"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">Transparent</option>
                  <option value="glass">Frosted Glass</option>
                  <option value="subtle-light">Subtle Light Canvas</option>
                  <option value="dark-slate">Dark Slate Canvas</option>
                  <option value="accent-tint">Accent Tint Gradient</option>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Logo Inner Padding</Label>
                <Select
                  value={hero.avatarPadding || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    handleFieldChange(
                      "avatarPadding",
                      e.target.value as NonNullable<HeroOverrides["avatarPadding"]>
                    )
                  }
                  className="h-8 text-xs bg-card"
                >
                  <option value="none">None (Full Bleed)</option>
                  <option value="sm">Compact (8px)</option>
                  <option value="md">Standard (16px)</option>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Typography & Subtitle Toggles ── */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Typography Hierarchy & Name Styling
          </h4>
          {!isPro && <ProBadge locked />}
        </div>

        {/* Hero Display Name Input */}
        <div className="space-y-1.5 p-3 rounded-xl bg-muted/40 border border-border/40">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Type className="h-3.5 w-3.5 text-primary" />
              <span>Hero Display Name</span>
            </Label>
            {fullName && hero.customName && hero.customName.trim() !== fullName.trim() && (
              <button
                type="button"
                onClick={() => handleFieldChange("customName", null)}
                className="text-[10px] text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                title="Reset to default account profile name"
              >
                Reset to default
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={hero.customName !== undefined && hero.customName !== null ? hero.customName : (fullName || "")}
              onChange={(e) => handleFieldChange("customName", e.target.value)}
              placeholder={fullName || "Enter display name..."}
              disabled={!isPro}
              maxLength={100}
              className="flex-1 h-8 px-3 text-xs bg-card rounded-lg border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
            />
            {onFullNameUpdate && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!isPro}
                onClick={() => {
                  const val = (hero.customName ?? fullName ?? "").trim();
                  if (val) {
                    onFullNameUpdate(val);
                    toast.success("Profile account name synchronized!");
                  }
                }}
                className="h-8 px-2.5 text-xs font-medium cursor-pointer shrink-0"
                title="Save this name globally to your account profile"
              >
                Sync to Account
              </Button>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Customize the name displayed in your hero headline (supports titles, credentials, or pen names).
          </p>
        </div>

        {/* Name Typography Scale & Alignment */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Name Display Scale</Label>
            <Select
              value={hero.nameSize || "lg"}
              disabled={!isPro}
              onChange={(e) =>
                handleFieldChange(
                  "nameSize",
                  e.target.value as NonNullable<HeroOverrides["nameSize"]>
                )
              }
              className="h-8 text-xs bg-card"
            >
              <option value="compact">Compact (1.25rem)</option>
              <option value="sm">Small (1.5rem)</option>
              <option value="md">Medium (2.0rem)</option>
              <option value="lg">Large (2.5rem)</option>
              <option value="xl">Extra Large (3.0rem)</option>
              <option value="2xl">2XL Display (3.75rem)</option>
              <option value="3xl">3XL Headline (4.5rem)</option>
              <option value="4xl">4XL Hero Massive (5.5rem)</option>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Name Text Alignment</Label>
            <div className="flex bg-muted/60 p-0.5 rounded-md border">
              {(["left", "center", "right"] as const).map((align) => (
                <button
                  key={align}
                  type="button"
                  disabled={!isPro}
                  onClick={() => handleFieldChange("nameAlign", align)}
                  className={`flex-1 py-1 flex items-center justify-center rounded text-xs transition-all ${
                    (hero.nameAlign || "center") === align
                      ? "bg-background shadow-xs font-semibold text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {align === "left" && <AlignLeft className="h-3.5 w-3.5" />}
                  {align === "center" && <AlignCenter className="h-3.5 w-3.5" />}
                  {align === "right" && <AlignRight className="h-3.5 w-3.5" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Font Weight & Visual Treatment */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Font Weight</Label>
            <Select
              value={hero.nameWeight || "extrabold"}
              disabled={!isPro}
              onChange={(e) =>
                handleFieldChange(
                  "nameWeight",
                  e.target.value as NonNullable<HeroOverrides["nameWeight"]>
                )
              }
              className="h-8 text-xs bg-card"
            >
              <option value="normal">Normal (400 - Light)</option>
              <option value="medium">Medium (500)</option>
              <option value="semibold">Semi-Bold (600)</option>
              <option value="bold">Strong Bold (700)</option>
              <option value="extrabold">Extra-Bold (800)</option>
              <option value="black">Ultra-Black (900)</option>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Visual Style & Treatment</Label>
            <Select
              value={hero.nameStyle || "default"}
              disabled={!isPro}
              onChange={(e) =>
                handleFieldChange(
                  "nameStyle",
                  e.target.value as NonNullable<HeroOverrides["nameStyle"]>
                )
              }
              className="h-8 text-xs bg-card"
            >
              <option value="default">Default (Theme Color)</option>
              <option value="gradient">Radiant Gradient Glow</option>
              <option value="italic">Academic Editorial Italic</option>
              <option value="uppercase">Executive All-Caps</option>
              <option value="underlined">Offset Accent Underline</option>
            </Select>
          </div>
        </div>

        {/* Letter Spacing & Custom Text Color */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Letter Spacing (Tracking)</Label>
            <Select
              value={hero.nameTracking || "normal"}
              disabled={!isPro}
              onChange={(e) =>
                handleFieldChange(
                  "nameTracking",
                  e.target.value as NonNullable<HeroOverrides["nameTracking"]>
                )
              }
              className="h-8 text-xs bg-card"
            >
              <option value="tight">Tight (-0.035em)</option>
              <option value="normal">Normal (Default)</option>
              <option value="wide">Wide (+0.05em)</option>
              <option value="widest">Ultra-Wide (+0.12em)</option>
            </Select>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">Custom Text Color</Label>
              {hero.nameColor && (
                <button
                  type="button"
                  onClick={() => handleFieldChange("nameColor", null)}
                  className="text-[10px] text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="color"
                disabled={!isPro}
                value={hero.nameColor || "#0f172a"}
                onChange={(e) => handleFieldChange("nameColor", e.target.value)}
                className="h-8 w-8 p-0.5 rounded border border-border cursor-pointer bg-card shrink-0"
              />
              <input
                type="text"
                disabled={!isPro}
                value={hero.nameColor || ""}
                placeholder="Theme text"
                onChange={(e) => handleFieldChange("nameColor", e.target.value || null)}
                className="flex-1 h-8 px-2 text-xs bg-card rounded border border-border outline-none font-mono"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Display Full Name</Label>
            <Switch
              checked={hero.nameVisible !== false}
              disabled={!isPro}
              onCheckedChange={(v) => handleFieldChange("nameVisible", v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Display Academic Title / Subtitle</Label>
            <Switch
              checked={hero.titleVisible !== false}
              disabled={!isPro}
              onCheckedChange={(v) => handleFieldChange("titleVisible", v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Display Bio in Header</Label>
            <Switch
              checked={hero.bioVisible !== false}
              disabled={!isPro}
              onCheckedChange={(v) => handleFieldChange("bioVisible", v)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
